/**
 * Gemeinsames Mapping der Kalender-API auf die Event-Form, die `EventModal`,
 * `EditEventModal` und die Kalenderansicht erwarten.
 *
 * Extrahiert aus `components/Calendar.tsx`, damit auch andere Seiten (z. B. das
 * Vorstands-Dashboard) dasselbe Modal mit derselben Datenform öffnen können.
 */
import apiClient from './apiClient';

/** Rohform eines Termins aus `GET /api/calendar`. */
export interface ApiCalendarEvent {
  id: number;
  title: string;
  start: string;
  start_time?: string | null;
  end_time?: string | null;
  color?: string | null;
  bookable?: number | string | null;
  booking_count?: number | string | null;
  creator?: { id?: number; name?: string; email?: string; image?: string | null } | null;
  cotrainer?: { id?: number; name?: string; email?: string; image?: string | null } | null;
  repeat?: unknown;
  repeat_end?: unknown;
  unique_id?: string | null;
  taetigkeitsbericht?: number | null;
  taetigkeitsbericht_text?: string | null;
}

/** Aufbereitete Form mit echten Date-Objekten und `extendedProps`. */
export interface CalendarEvent {
  id: number;
  title: string;
  start: Date;
  end: Date;
  extendedProps: {
    color?: string | null;
    bookable: number;
    bookingCount: number;
    creator: { name: string; id?: number; email?: string; image?: string | null };
    cotrainer: { name: string; id?: number; email?: string; image?: string | null } | null;
    repeat?: unknown;
    repeat_end?: unknown;
    unique_id?: string | null;
    taetigkeitsbericht: number;
    taetigkeitsbericht_text: string;
  };
}

/** 'HH:mm' → [Stunde, Minute]; fehlende/ungültige Zeit wird 00:00. */
function parseTime(value?: string | null): [number, number] {
  const [h, m] = String(value || '').split(':');
  const hour = Number.parseInt(h, 10);
  const minute = Number.parseInt(m, 10);
  return [Number.isFinite(hour) ? hour : 0, Number.isFinite(minute) ? minute : 0];
}

/** API-Termin in die Form bringen, die die Event-Modals erwarten. */
export function mapApiEvent(event: ApiCalendarEvent): CalendarEvent {
  const startDate = new Date(event.start);
  const [startHour, startMinute] = parseTime(event.start_time);
  const [endHour, endMinute] = parseTime(event.end_time);

  const startDateTime = new Date(startDate);
  startDateTime.setHours(startHour, startMinute, 0, 0);

  const endDateTime = new Date(startDate);
  endDateTime.setHours(endHour, endMinute, 0, 0);
  // Ohne Endzeit (oder bei fehlerhaften Daten) mindestens die Startzeit verwenden.
  if (endDateTime < startDateTime) endDateTime.setTime(startDateTime.getTime());

  return {
    id: event.id,
    title: event.title,
    start: startDateTime,
    end: endDateTime,
    extendedProps: {
      color: event.color,
      bookable: Number.parseInt(String(event.bookable), 10) || 0,
      bookingCount: Number.parseInt(String(event.booking_count), 10) || 0,
      creator: {
        name: event.creator?.name || event.creator?.id?.toString() || 'Unbekannt',
        id: event.creator?.id,
        email: event.creator?.email,
        image: event.creator?.image,
      },
      cotrainer: event.cotrainer
        ? {
            name: event.cotrainer.name || event.cotrainer.id?.toString() || 'Unbekannt',
            id: event.cotrainer.id,
            email: event.cotrainer.email,
            image: event.cotrainer.image,
          }
        : null,
      repeat: event.repeat,
      repeat_end: event.repeat_end,
      unique_id: event.unique_id,
      taetigkeitsbericht: event.taetigkeitsbericht || 0,
      taetigkeitsbericht_text: event.taetigkeitsbericht_text || '',
    },
  };
}

/**
 * Einen einzelnen Termin laden und aufbereiten.
 *
 * `GET /api/calendar` hat keinen Einzel-Endpoint, daher wird die Liste geholt und
 * clientseitig gefiltert — dieselbe Quelle, die auch die Kalenderansicht nutzt.
 * Gibt zusätzlich alle Termine desselben Tages zurück, damit `EventModal` wie im
 * Kalender zwischen ihnen wechseln kann.
 */
export async function fetchCalendarEvent(
  id: number,
): Promise<{ event: CalendarEvent; sameDay: CalendarEvent[] } | null> {
  const res = await apiClient.get('/api/calendar');
  if (!res.data?.success || !Array.isArray(res.data.events)) return null;

  const raw = (res.data.events as ApiCalendarEvent[]).find((e) => e.id === id);
  if (!raw) return null;

  const event = mapApiEvent(raw);
  const day = String(raw.start).slice(0, 10);
  const sameDay = (res.data.events as ApiCalendarEvent[])
    .filter((e) => String(e.start).slice(0, 10) === day)
    .map(mapApiEvent)
    .sort((a, b) => a.start.getTime() - b.start.getTime());

  return { event, sameDay };
}
